import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  Bell,
  BookOpen,
  CalendarDays,
  CalendarClock,
  ChevronLeft,
  CheckCheck,
  FileText,
  FlaskConical,
  Headphones,
  HelpCircle,
  Home,
  LogOut,
  Menu,
  MessageCircle,
  PanelLeftClose,
  PanelLeftOpen,
  Package,
  Plus,
  ReceiptText,
  Search,
  SendHorizontal,
  ShieldCheck,
  ShoppingBag,
  X,
  UserRound,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { apiGet, apiPost } from '../lib/api'
import { C } from '../lib/tokens'
import { Avatar, Logo } from './UI'
import { SkeletonRows } from './PortalUX'
import PortalWalkthrough from './PortalWalkthrough'
import PushNotificationPrompt from './PushNotificationPrompt'
import { TOPICS as PATIENT_HELP_TOPICS } from '../lib/patientHelpTopics'
import { patientHidesShopTab } from '../lib/portalAccess'
import { teamSenderShortName } from '../lib/teamSenderName'

const LEGACY_NAV = [
  { id: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: Home },
  { id: 'visits', path: '/visits', label: 'Visits', icon: CalendarDays },
  { id: 'messages', path: '/messages', label: 'Messages', icon: MessageCircle },
  { id: 'prescriptions', path: '/prescriptions', label: 'Prescriptions', icon: ShieldCheck },
  { id: 'labs', path: '/labs', label: 'Labs', icon: FlaskConical },
  { id: 'orders', path: '/orders', label: 'Orders', icon: ReceiptText },
  { id: 'forms', path: '/forms', label: 'Forms', icon: FileText },
]

const MSO_NAV = [
  { id: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: Home, match: ['/dashboard'] },
  { id: 'shop', path: '/shop', label: 'Shop', icon: ShoppingBag, match: ['/shop', '/intake', '/refill'] },
  { id: 'visits', path: '/visits', label: 'Visits', icon: CalendarDays, match: ['/visits'] },
  { id: 'messages', path: '/messages', label: 'Messages', icon: MessageCircle, match: ['/messages'] },
  { id: 'prescriptions', path: '/prescriptions', label: 'Prescriptions', icon: ShieldCheck, match: ['/prescriptions'] },
  { id: 'labs', path: '/labs', label: 'Labs', icon: FlaskConical, match: ['/labs'] },
  { id: 'orders', path: '/orders', label: 'Orders', icon: ReceiptText, match: ['/orders'] },
  { id: 'help', path: '/help', label: 'Help Center', icon: HelpCircle, match: ['/help'] },
  { id: 'account', path: '/account', label: 'Account', icon: UserRound, match: ['/account'] },
]

const NOTIFICATION_ICONS = {
  message: MessageCircle,
  order: Package,
  visit: CalendarClock,
  account: UserRound,
  prescription: ShieldCheck,
}
const NOTIFICATION_READ_STORAGE_KEY = 'kis_rx_read_notification_keys_v1'
const ATLAS_FEATURED_ARTICLE_IDS = ['labs-upload-labs', 'labs-biobox', 'support-message-details', 'orders-order-status']
const ATLAS_ARTICLES = PATIENT_HELP_TOPICS.flatMap(topic => (
  topic.articles.map(article => ({
    id: `${topic.id}-${article.id}`,
    topic: { id: topic.id, title: topic.title, description: topic.description },
    title: article.title,
    summary: article.summary,
    steps: article.steps || [],
    links: article.links || [],
  }))
))
const ATLAS_REQUEST_CONFIGS = {
  product_request: {
    title: 'Request a Product',
    subtitle: 'Ask KIS Rx to review a product you would like available in the portal.',
    icon: Package,
    threadKey: 'atlas:product-request',
    threadLabel: 'Product request',
    requestType: 'rx_product_request',
    requestCategory: 'portal_product_request',
    accent: C.blue,
    titlePlaceholder: 'Example: Add NAD+ injections to my portal',
    detailsPlaceholder: 'Tell us the product name, why you are interested, and any context that would help the team review it.',
    submitLabel: 'Submit product request',
    sentLabel: 'Product request sent',
  },
  bug_report: {
    title: 'Report a Bug',
    subtitle: 'Tell us when something looks broken or does not work as expected.',
    icon: AlertCircle,
    threadKey: 'atlas:bug-report',
    threadLabel: 'Bug report',
    requestType: 'bug',
    requestCategory: 'patient_portal_bug',
    accent: C.red,
    titlePlaceholder: 'Example: Lab upload button is not working',
    detailsPlaceholder: 'What happened? What did you expect? Include steps to reproduce it if you can.',
    submitLabel: 'Submit bug report',
    sentLabel: 'Bug report sent',
  },
  feature_request: {
    title: 'Request a Feature',
    subtitle: 'Suggest an improvement you would like added to the patient portal.',
    icon: Plus,
    threadKey: 'atlas:feature-request',
    threadLabel: 'Feature request',
    requestType: 'feature_request',
    requestCategory: 'patient_portal_feature',
    accent: C.green,
    titlePlaceholder: 'Example: Add autoship reminders',
    detailsPlaceholder: 'Describe the feature and how it would make the portal easier or more useful for you.',
    submitLabel: 'Submit feature request',
    sentLabel: 'Feature request sent',
  },
}
const ATLAS_ACTIONS = [
  {
    id: 'answers',
    label: 'ATLAS Answers',
    description: 'Answers from Patient Help Center, your records, labs, orders, and live shop products.',
    icon: MessageCircle,
    tone: C.purple,
  },
  {
    id: 'docs',
    label: 'Help Docs',
    description: 'Search Patient Help Center guides.',
    icon: Search,
    tone: C.blue,
  },
  {
    id: 'support',
    label: 'Need Help',
    description: 'Message support about orders, labs, billing, uploads, forms, or account questions.',
    icon: Headphones,
    tone: '#1C2B33',
  },
  {
    id: 'product_request',
    label: 'Request Product',
    description: 'Ask for a product you would like to see available in the portal.',
    icon: Package,
    tone: C.blue,
  },
  {
    id: 'bug_report',
    label: 'Report Bug',
    description: 'Tell us if something is broken, confusing, or not working correctly.',
    icon: AlertCircle,
    tone: C.red,
  },
  {
    id: 'feature_request',
    label: 'Request Feature',
    description: 'Suggest a new portal feature or improvement.',
    icon: Plus,
    tone: C.green,
  },
]

function NavItem({ item, active, collapsed, onClick }) {
  const Icon = item.icon
  return (
    <Link
      key={item.id}
      className={`kis-nav-item${active ? ' kis-nav-item-active' : ''}`}
      to={item.path}
      data-tour-id={item.id}
      onClick={(event) => {
        if (active && item.id === 'shop') {
          window.dispatchEvent(new CustomEvent('kis:shop:reset'))
        }
        onClick?.(event)
      }}
      title={collapsed ? item.label : ''}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 44,
        padding: collapsed ? '0 10px' : '0 14px',
        margin: '4px 0',
        borderRadius: 16,
        background: active ? C.navActive : 'transparent',
        color: active ? C.text : C.textSecondary,
        fontSize: 14,
        fontWeight: active ? 820 : 690,
        letterSpacing: 0,
        textDecoration: 'none',
        justifyContent: collapsed ? 'center' : 'flex-start',
        boxShadow: active ? 'inset 0 0 0 1px rgba(29,29,31,0.075), 0 10px 24px rgba(17,24,39,0.055)' : 'none',
      }}
    >
      <span className="kis-nav-icon" aria-hidden="true">
        <Icon size={18} strokeWidth={active ? 2.35 : 1.9} color={active ? C.greenDark : C.textTertiary} />
      </span>
      {!collapsed && <span>{item.label}</span>}
    </Link>
  )
}

function notificationTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function dashboardHeaderGreeting(patient) {
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const nameSource = patient?.first_name || patient?.firstName || patient?.full_name || patient?.fullName || patient?.email || 'there'
  const firstName = String(nameSource).split(/\s+/)[0] || 'there'
  return `${greeting}, ${firstName}.`
}

function dashboardHeaderDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

function patientDisplayName(patient) {
  return patient?.full_name || patient?.fullName || [patient?.first_name, patient?.last_name].filter(Boolean).join(' ').trim() || 'You'
}

function supportMessageThreadKey(message = {}) {
  return message.thread_key ||
    message.metadata?.threadKey ||
    message.metadata?.thread_key ||
    'support'
}

function atlasArticleText(article = {}) {
  return [
    article.title,
    article.summary,
    article.topic?.title,
    article.topic?.description,
    ...(article.steps || []),
  ].join(' ').toLowerCase()
}

function atlasArticlesForQuery(query) {
  const normalized = query.trim().toLowerCase()
  if (!normalized) {
    return ATLAS_FEATURED_ARTICLE_IDS
      .slice(0, 3)
      .map(id => ATLAS_ARTICLES.find(article => article.id === id))
      .filter(Boolean)
  }
  return ATLAS_ARTICLES
    .filter(article => atlasArticleText(article).includes(normalized))
    .slice(0, 4)
}

function messageTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function linkifyText(text, mine = false) {
  const value = String(text || '')
  const pattern = /(https?:\/\/[^\s<>"']+)/gi
  const parts = []
  let lastIndex = 0
  let match
  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) parts.push(value.slice(lastIndex, match.index))
    const href = match[0].replace(/[),.;!?]+$/, '')
    const trailing = match[0].slice(href.length)
    parts.push(
      <a
        key={`${href}-${match.index}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        style={{ color: mine ? '#FFFFFF' : C.blue, fontWeight: 720, textDecoration: 'underline', textUnderlineOffset: 2, overflowWrap: 'anywhere' }}
      >
        {href}
      </a>
    )
    if (trailing) parts.push(trailing)
    lastIndex = pattern.lastIndex
  }
  if (lastIndex < value.length) parts.push(value.slice(lastIndex))
  return parts.length ? parts : value
}

function SupportBubbleMessage({ message }) {
  const mine = message.sender_type === 'patient'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', gap: 4 }}>
      {!mine && (
        <div style={{ padding: '0 4px', fontSize: 11.5, color: C.textTertiary, fontWeight: 720 }}>
          {teamSenderShortName(message.sender_name)}
        </div>
      )}
      <div style={{
        maxWidth: '88%',
        borderRadius: mine ? '18px 18px 5px 18px' : '18px 18px 18px 5px',
        background: mine ? C.blue : '#F2F2F7',
        color: mine ? '#FFFFFF' : C.text,
        padding: '10px 12px',
        fontSize: 13.5,
        lineHeight: 1.42,
        overflowWrap: 'anywhere',
        boxShadow: mine ? '0 8px 18px rgba(0, 122, 255, 0.18)' : 'none',
      }}>
        {linkifyText(message.body || message.content, mine)}
      </div>
      <div style={{ padding: mine ? '0 4px 0 0' : '0 0 0 4px', fontSize: 11, color: C.textTertiary }}>
        {message.__pending ? 'Sending...' : messageTime(message.created_at)}
      </div>
    </div>
  )
}

function AtlasIconMark({ size = 38, active = false }) {
  const badgeSize = Math.max(14, Math.round(size * 0.38))
  return (
    <span style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      background: active
        ? 'linear-gradient(180deg, #E7F2FF 0%, #DDF8EB 100%)'
        : 'linear-gradient(180deg, #EAFBF2 0%, #E8F2FF 100%)',
      color: active ? C.blue : C.greenDark,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      position: 'relative',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.72)',
    }}>
      <MessageCircle size={Math.round(size * 0.52)} strokeWidth={2.35} />
      <span style={{
        position: 'absolute',
        right: -1,
        bottom: -1,
        width: badgeSize,
        height: badgeSize,
        borderRadius: badgeSize / 2,
        background: C.green,
        color: '#FFFFFF',
        border: '2px solid #FFFFFF',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Plus size={Math.round(badgeSize * 0.58)} strokeWidth={3} />
      </span>
    </span>
  )
}

function AtlasActionButton({ action, onClick }) {
  const Icon = action.icon
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        minHeight: 82,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        background: '#FFFFFF',
        color: C.text,
        display: 'grid',
        gridTemplateColumns: '48px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: 13,
        padding: 13,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.045)',
      }}
    >
      <span style={{
        width: 48,
        height: 48,
        borderRadius: 13,
        background: `${action.tone}14`,
        color: action.tone,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon size={20} strokeWidth={2.2} />
      </span>
      <span style={{ minWidth: 0, display: 'grid', gap: 2 }}>
        <strong style={{ fontSize: 14, color: C.text, lineHeight: 1.2 }}>{action.label}</strong>
        <span style={{ fontSize: 12.3, color: C.textSecondary, lineHeight: 1.35 }}>{action.description}</span>
      </span>
      <ArrowRight size={17} color={C.textTertiary} />
    </button>
  )
}

function AtlasArticlePreview({ article, open, onToggle }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: '#FFFFFF', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%',
          border: 'none',
          background: open ? '#FAFCFF' : '#FFFFFF',
          color: C.text,
          cursor: 'pointer',
          display: 'grid',
          gridTemplateColumns: '28px minmax(0, 1fr) auto',
          gap: 10,
          padding: 12,
          textAlign: 'left',
          alignItems: 'start',
        }}
      >
        <span style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: '#EAF8F1',
          color: C.greenDark,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <BookOpen size={15} strokeWidth={2.15} />
        </span>
        <span style={{ minWidth: 0, display: 'grid', gap: 3 }}>
          <strong style={{ fontSize: 13.2, lineHeight: 1.25, color: C.text }}>{article.title}</strong>
          <span style={{ fontSize: 11.6, color: C.textTertiary, fontWeight: 740 }}>{article.topic?.title}</span>
        </span>
        <ArrowRight
          size={15}
          color={C.textTertiary}
          style={{ marginTop: 5, transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.16s ease' }}
        />
      </button>
      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '10px 12px 12px', display: 'grid', gap: 10 }}>
          <p style={{ margin: 0, color: C.textSecondary, fontSize: 12.6, lineHeight: 1.42 }}>{article.summary}</p>
          {article.steps?.length ? (
            <ol style={{ margin: 0, paddingLeft: 17, display: 'grid', gap: 5, color: C.textSecondary, fontSize: 12.2, lineHeight: 1.38 }}>
              {article.steps.slice(0, 4).map(step => <li key={step}>{step}</li>)}
            </ol>
          ) : null}
        </div>
      )}
    </div>
  )
}

function AtlasAnswerChatMessage({ message }) {
  const mine = message.role === 'user'
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <div style={{
        maxWidth: mine ? '82%' : '92%',
        display: 'grid',
        gridTemplateColumns: mine ? 'minmax(0, 1fr)' : '30px minmax(0, 1fr)',
        gap: 9,
        alignItems: 'start',
      }}>
        {!mine && (
          <span style={{ width: 30, height: 30, borderRadius: 15, background: '#F0EAFE', color: C.purple, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <MessageCircle size={15} strokeWidth={2.2} />
          </span>
        )}
        <div style={{
          border: mine ? 'none' : `1px solid ${C.border}`,
          borderRadius: mine ? '18px 18px 4px 18px' : 8,
          background: mine ? C.blue : '#FFFFFF',
          color: mine ? '#FFFFFF' : C.text,
          padding: mine ? '10px 13px' : '11px 12px',
          boxShadow: mine ? '0 8px 18px rgba(0, 122, 255, 0.16)' : '0 8px 18px rgba(15, 23, 42, 0.035)',
          minWidth: 0,
        }}>
          {!mine && <strong style={{ display: 'block', marginBottom: 4, fontSize: 12.5, color: C.text }}>ATLAS</strong>}
          <div style={{ fontSize: 13, lineHeight: 1.45, color: mine ? '#FFFFFF' : C.textSecondary, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {linkifyText(message.content, mine)}
          </div>
          {!mine && message.articles?.length ? (
            <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
              {message.articles.slice(0, 2).map(article => (
                <div key={article.id || article.title} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 9px', background: '#F8FAFC' }}>
                  <div style={{ fontSize: 12.2, fontWeight: 780, color: C.text }}>{article.title}</div>
                  {article.category?.title && <div style={{ marginTop: 2, fontSize: 11.2, color: C.textTertiary }}>{article.category.title}</div>}
                </div>
              ))}
            </div>
          ) : null}
          {!mine && message.products?.length ? (
            <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
              {message.products.slice(0, 3).map(product => {
                const href = product.url || product.shopUrl || '/shop'
                return (
                  <a
                    key={product.id || product.sku || product.name}
                    href={href}
                    style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 9px', background: '#F7FBFF', textDecoration: 'none', color: C.text, display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr) auto', gap: 8, alignItems: 'center' }}
                  >
                    <Package size={15} color={C.blue} strokeWidth={2.2} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 12.2, fontWeight: 800, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</span>
                      <span style={{ display: 'block', marginTop: 2, fontSize: 11.2, color: C.textTertiary }}>{[product.category, product.priceLabel].filter(Boolean).join(' - ')}</span>
                    </span>
                    <ArrowRight size={14} color={C.textTertiary} />
                  </a>
                )
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function SupportBubble({ patient, open: controlledOpen, onOpenChange, hideTrigger = false }) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [view, setView] = useState('home')
  const [messages, setMessages] = useState([])
  const [body, setBody] = useState('')
  const [answerMessages, setAnswerMessages] = useState([])
  const [answerQuestion, setAnswerQuestion] = useState('')
  const [answerLoading, setAnswerLoading] = useState(false)
  const [answerError, setAnswerError] = useState('')
  const [articleQuery, setArticleQuery] = useState('')
  const [openArticleId, setOpenArticleId] = useState('')
  const [requestTitle, setRequestTitle] = useState('')
  const [requestDetails, setRequestDetails] = useState('')
  const [requestSending, setRequestSending] = useState(false)
  const [requestError, setRequestError] = useState('')
  const [requestSent, setRequestSent] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sourceUnavailable, setSourceUnavailable] = useState(false)
  const rootRef = useRef(null)
  const scrollRef = useRef(null)
  const answerScrollRef = useRef(null)
  const mountedRef = useRef(false)
  const loadingRef = useRef(false)
  const isControlledOpen = typeof controlledOpen === 'boolean'
  const open = isControlledOpen ? controlledOpen : internalOpen
  const setOpen = useCallback((nextOpen) => {
    if (isControlledOpen) {
      onOpenChange?.(typeof nextOpen === 'function' ? nextOpen(open) : nextOpen)
      return
    }
    setInternalOpen(nextOpen)
  }, [isControlledOpen, onOpenChange, open])

  const loadSupportMessages = useCallback(async ({ showLoading = false, force = false } = {}) => {
    if (loadingRef.current && !force) return
    loadingRef.current = true
    if (showLoading) setLoading(true)
    try {
      const supportResult = await apiGet('/api/mso/support-messages')
      if (!mountedRef.current) return

      setMessages(supportResult.messages || [])
      setSourceUnavailable(Boolean(supportResult.sourceUnavailable))
    } catch {
      if (mountedRef.current) {
        setSourceUnavailable(true)
      }
    } finally {
      loadingRef.current = false
      if (mountedRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    const initialLoad = window.setTimeout(() => loadSupportMessages({ force: true }), 0)
    const interval = window.setInterval(() => {
      if (!document.hidden) loadSupportMessages()
    }, 30000)
    const onRefresh = () => loadSupportMessages({ force: true })
    window.addEventListener('kis:notifications:refresh', onRefresh)
    return () => {
      window.clearTimeout(initialLoad)
      window.clearInterval(interval)
      window.removeEventListener('kis:notifications:refresh', onRefresh)
    }
  }, [loadSupportMessages])

  useEffect(() => {
    if (!open) return undefined
    const initialLoad = window.setTimeout(() => loadSupportMessages({ showLoading: true, force: true }), 0)
    const interval = window.setInterval(() => {
      if (!document.hidden) loadSupportMessages()
    }, 12000)
    return () => {
      window.clearTimeout(initialLoad)
      window.clearInterval(interval)
    }
  }, [open, loadSupportMessages])

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    const onKeyDown = event => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, setOpen])

  const supportThreadMessages = useMemo(() => {
    return messages.filter(message => supportMessageThreadKey(message) === 'support')
  }, [messages])
  const visibleAtlasArticles = useMemo(() => atlasArticlesForQuery(articleQuery), [articleQuery])
  const activeRequestConfig = ATLAS_REQUEST_CONFIGS[view] || null

  useEffect(() => {
    if (!open || view !== 'support') return
    scrollRef.current?.scrollIntoView({ block: 'end' })
  }, [open, view, supportThreadMessages.length])

  useEffect(() => {
    if (!open || view !== 'answers') return
    answerScrollRef.current?.scrollIntoView({ block: 'end' })
  }, [open, view, answerMessages.length, answerLoading])

  const unreadCount = useMemo(() => {
    return messages.filter(message => message.sender_type === 'mso' && !message.read_by_patient).length
  }, [messages])
  const supportUnreadCount = useMemo(() => {
    return supportThreadMessages.filter(message => message.sender_type === 'mso' && !message.read_by_patient).length
  }, [supportThreadMessages])

  const totalUnreadCount = unreadCount
  const fullMessagesHref = '/messages?thread=support'

  useEffect(() => {
    if (!open || view !== 'support') return undefined
    if (supportUnreadCount <= 0) return undefined

    const markRead = window.setTimeout(() => {
      const thread = 'support'
      setMessages(current => current.map(message =>
        supportMessageThreadKey(message) === 'support' && message.sender_type === 'mso' ? { ...message, read_by_patient: true } : message
      ))
      apiPost('/api/mso/notifications/read', { thread })
        .finally(() => {
          window.dispatchEvent(new CustomEvent('kis:thread-read', { detail: { thread } }))
          window.dispatchEvent(new CustomEvent('kis:notifications:refresh'))
      })
    }, 0)
    return () => window.clearTimeout(markRead)
  }, [open, view, supportUnreadCount])

  const canSend = body.trim().length > 0 && !sending
  const canAskAtlas = answerQuestion.trim().length > 0 && !answerLoading
  const requestCanSend = Boolean(activeRequestConfig) && (requestTitle.trim().length > 0 || requestDetails.trim().length > 0) && !requestSending

  const openAtlasView = useCallback((nextView = 'home') => {
    setView(nextView)
    setRequestError('')
    setRequestSent('')
    if (!ATLAS_REQUEST_CONFIGS[nextView]) {
      setRequestTitle('')
      setRequestDetails('')
    }
  }, [])

  useEffect(() => {
    const onOpenPortalSupport = event => {
      setOpen(true)
      openAtlasView(event.detail?.view || 'home')
    }
    window.addEventListener('kis:portal-support:open', onOpenPortalSupport)
    return () => window.removeEventListener('kis:portal-support:open', onOpenPortalSupport)
  }, [openAtlasView, setOpen])

  const askAtlasAnswer = async () => {
    const question = answerQuestion.trim()
    if (!question || answerLoading) return

    const userMessage = {
      id: `atlas-user-${Date.now()}`,
      role: 'user',
      content: question,
    }
    const conversation = [...answerMessages, userMessage].map(message => ({
      role: message.role === 'assistant' ? 'assistant' : 'user',
      content: message.content,
    }))

    setAnswerMessages(current => [...current, userMessage])
    setAnswerQuestion('')
    setAnswerError('')
    setAnswerLoading(true)
    try {
      const result = await apiPost('/api/mso/atlas-answer', {
        question,
        pageUrl: window.location.href,
        pageTitle: document.title,
        conversation,
      })
      setAnswerMessages(current => [...current, {
        id: `atlas-answer-${Date.now()}`,
        role: 'assistant',
        content: result.answer || 'I could not find an answer yet. Contact support and we will help.',
        articles: result.recommendedArticles || [],
        products: result.recommendedProducts || [],
      }])
    } catch (err) {
      setAnswerError(err.message || 'ATLAS could not answer that yet.')
      setAnswerMessages(current => [...current, {
        id: `atlas-answer-error-${Date.now()}`,
        role: 'assistant',
        content: 'I could not answer that right now. Contact support and we will route it to the care team.',
      }])
    } finally {
      setAnswerLoading(false)
    }
  }

  const sendSupportMessage = async () => {
    const text = body.trim()
    if (!text || sending) return

    const optimistic = {
      id: `local-support-${Date.now()}`,
      body: text,
      sender_type: 'patient',
      sender_name: patientDisplayName(patient),
      metadata: {
        threadKey: 'support',
        threadLabel: 'Support Team',
        routeTarget: 'kisrx',
      },
      thread_key: 'support',
      created_at: new Date().toISOString(),
      __pending: true,
    }
    setBody('')
    setError('')
    setSending(true)
    setMessages(current => [...current, optimistic])
    try {
      await apiPost('/api/mso/support-messages', {
        body: text,
        attachments: [],
        threadKey: 'support',
        metadata: {
          threadKey: 'support',
          threadLabel: 'Support Team',
          routeTarget: 'kisrx',
          source: 'patient_portal_atlas_support',
        },
      })
      await loadSupportMessages({ force: true })
      window.dispatchEvent(new CustomEvent('kis:notifications:refresh'))
    } catch (err) {
      setMessages(current => current.filter(message => message.id !== optimistic.id))
      setBody(text)
      setError(err.message || 'Could not send that message.')
    } finally {
      setSending(false)
    }
  }

  const sendAtlasRequest = async () => {
    const config = activeRequestConfig
    if (!config || !requestCanSend) return

    const summary = requestTitle.trim()
    const details = requestDetails.trim()
    const ticketSummary = summary || details.split(/\n/)[0]?.slice(0, 160) || config.threadLabel
    const text = [
      `[${config.threadLabel}]`,
      summary ? `Summary: ${summary}` : '',
      details ? `Details:\n${details}` : '',
    ].filter(Boolean).join('\n\n')

    setRequestSending(true)
    setRequestError('')
    setRequestSent('')
    try {
      await apiPost('/api/mso/support-messages', {
        body: text,
        attachments: [],
        threadKey: config.threadKey,
        metadata: {
          threadKey: config.threadKey,
          threadLabel: config.threadLabel,
          routeTarget: 'agency_ticketing',
          requestType: config.requestType,
          requestCategory: config.requestCategory,
          requestSummary: ticketSummary,
          requestDetails: details,
          ticketLabel: config.threadLabel,
          pageUrl: window.location.href,
          pageTitle: document.title,
          source: 'patient_portal_atlas',
        },
      })
      setRequestTitle('')
      setRequestDetails('')
      setRequestSent(config.sentLabel)
      await loadSupportMessages({ force: true })
      window.dispatchEvent(new CustomEvent('kis:notifications:refresh'))
    } catch (err) {
      setRequestError(err.message || 'Could not send that request.')
    } finally {
      setRequestSending(false)
    }
  }

  const onComposerKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      sendSupportMessage()
    }
  }

  const onAtlasAnswerKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      askAtlasAnswer()
    }
  }

  const ActiveRequestIcon = activeRequestConfig?.icon
  const atlasViewCopy = {
    answers: { title: 'ATLAS Answers', subtitle: 'Patient Help Center, records, labs, orders, and shop.' },
    docs: { title: 'Help Docs', subtitle: 'Search the Patient Help Center.' },
    support: { title: 'KIS Rx Support', subtitle: 'Get help with orders, labs, billing, uploads, forms, or account questions.' },
  }[view]
  const panelTitle = activeRequestConfig?.title || atlasViewCopy?.title || 'KIS Rx Atlas'
  const panelSubtitle = activeRequestConfig?.subtitle || atlasViewCopy?.subtitle || 'Help articles and care support.'

  return (
    <div ref={rootRef} className="kis-support-bubble-root" style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 75 }}>
      {open && (
        <div className="kis-support-bubble-panel" style={{
          position: 'absolute',
          right: 0,
          bottom: 72,
          width: 'min(430px, calc(100vw - 32px))',
          height: 'min(650px, calc(100vh - 118px))',
          borderRadius: 24,
          border: `1px solid ${C.border}`,
          background: 'rgba(255,255,255,0.88)',
          boxShadow: C.shadowStrong,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backdropFilter: 'blur(28px) saturate(1.16)',
        }}>
          {view === 'home' ? (
            <div style={{
              padding: 20,
              background: 'linear-gradient(135deg, #075F45 0%, #0B8F6B 52%, #2378FF 100%)',
              color: '#FFFFFF',
              display: 'grid',
              gap: 18,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                  <AtlasIconMark size={40} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15.5, fontWeight: 900, letterSpacing: 0 }}>ATLAS</span>
                    <span style={{ display: 'block', marginTop: 2, fontSize: 12.2, fontWeight: 760, opacity: 0.86 }}>KIS Rx Care Logic & Support</span>
                  </span>
                </div>
                <button
                  type="button"
                  aria-label="Close Atlas"
                  onClick={() => setOpen(false)}
                  style={{
                    width: 36,
                    height: 36,
                    border: '1px solid rgba(255,255,255,0.28)',
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.14)',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <h2 style={{ margin: 0, fontSize: 25, lineHeight: 1.08, fontWeight: 920, letterSpacing: 0 }}>How can we help?</h2>
                <p style={{ margin: 0, maxWidth: 360, fontSize: 13.5, lineHeight: 1.45, fontWeight: 690, opacity: 0.94 }}>
                  Use this widget to get help, request a product in the portal, report a bug, or request a feature. Page context is attached automatically.
                </p>
              </div>
            </div>
          ) : (
            <div style={{
              padding: '13px 16px 12px',
              borderBottom: `1px solid ${C.border}`,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.9) 100%)',
              display: 'grid',
              gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <button
                    type="button"
                    aria-label="Back to Atlas"
                    onClick={() => openAtlasView('home')}
                    style={{
                      width: 32,
                      height: 32,
                      border: `1px solid ${C.border}`,
                      borderRadius: 16,
                      background: 'rgba(255,255,255,0.8)',
                      color: C.textSecondary,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ChevronLeft size={17} strokeWidth={2.3} />
                  </button>
                  <AtlasIconMark size={38} active />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: 820, color: C.text }}>{panelTitle}</span>
                    <span style={{ display: 'block', marginTop: 3, fontSize: 12.3, color: C.textSecondary }}>{panelSubtitle}</span>
                  </span>
                </div>
                <button
                  type="button"
                  aria-label="Close Atlas"
                  onClick={() => setOpen(false)}
                  style={{
                    width: 34,
                    height: 34,
                    border: 'none',
                    borderRadius: 17,
                    background: 'rgba(242,243,247,0.84)',
                    color: C.textSecondary,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <X size={17} strokeWidth={2.2} />
                </button>
              </div>
            </div>
          )}

          {view === 'home' && (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'grid', alignContent: 'start', gap: 12, background: '#F8FAFC' }}>
              <section style={{ display: 'grid', gap: 12 }}>
                {ATLAS_ACTIONS.map(action => (
                  <AtlasActionButton
                    key={action.id}
                    action={action}
                    onClick={() => openAtlasView(action.id)}
                  />
                ))}
              </section>
            </div>
          )}

          {view === 'docs' && (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, display: 'grid', alignContent: 'start', gap: 12, background: '#F8FAFC' }}>
              <label style={{ position: 'relative', display: 'block' }}>
                <Search size={16} color={C.textTertiary} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="search"
                  value={articleQuery}
                  onChange={event => setArticleQuery(event.target.value)}
                  placeholder="Search help articles..."
                  style={{
                    width: '100%',
                    minHeight: 42,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    background: '#FFFFFF',
                    color: C.text,
                    padding: '0 14px 0 40px',
                    fontSize: 14,
                    boxSizing: 'border-box',
                    outlineColor: C.blue,
                  }}
                />
              </label>
              <section style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '0 2px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <BookOpen size={16} color={C.greenDark} />
                    <strong style={{ color: C.text, fontSize: 13.8 }}>Help articles</strong>
                  </div>
                  <Link to="/help" onClick={() => setOpen(false)} style={{ color: C.blue, fontSize: 12.2, fontWeight: 780, textDecoration: 'none' }}>
                    View all
                  </Link>
                </div>
                {visibleAtlasArticles.length ? (
                  visibleAtlasArticles.map(article => (
                    <AtlasArticlePreview
                      key={`${article.topic?.id}-${article.id}`}
                      article={article}
                      open={openArticleId === article.id}
                      onToggle={() => setOpenArticleId(current => current === article.id ? '' : article.id)}
                    />
                  ))
                ) : (
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: '#FFFFFF', padding: 14, color: C.textSecondary, fontSize: 12.8, lineHeight: 1.4 }}>
                    No articles match that search. Try labs, BioBox, orders, billing, support, or password.
                  </div>
                )}
              </section>
            </div>
          )}

          {view === 'answers' && (
            <>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)' }}>
                {answerMessages.length ? (
                  answerMessages.map(message => (
                    <AtlasAnswerChatMessage key={message.id} message={message} />
                  ))
                ) : (
                  <div style={{
                    margin: 'auto 0',
                    padding: 18,
                    borderRadius: 8,
                    background: '#FFFFFF',
                    border: `1px solid ${C.border}`,
                    color: C.textSecondary,
                    fontSize: 13,
                    lineHeight: 1.45,
                    textAlign: 'center',
                  }}>
                    Ask ATLAS about your portal, labs, prescriptions, orders, statuses, or live shop products.
                  </div>
                )}
                {answerLoading && (
                  <AtlasAnswerChatMessage message={{ id: 'atlas-loading', role: 'assistant', content: 'Checking Patient Help Center and your portal records...' }} />
                )}
                <div ref={answerScrollRef} />
              </div>
              <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(247,248,250,0.92)', padding: 12, display: 'grid', gap: 9 }}>
                {answerError && <div style={{ fontSize: 12, color: C.red }}>{answerError}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, padding: 5, border: `1px solid ${C.border}`, borderRadius: 22, background: '#FFFFFF', alignItems: 'end' }}>
                  <textarea
                    value={answerQuestion}
                    onChange={event => setAnswerQuestion(event.target.value)}
                    onKeyDown={onAtlasAnswerKeyDown}
                    rows={1}
                    placeholder="Ask ATLAS"
                    style={{
                      minHeight: 36,
                      maxHeight: 96,
                      resize: 'none',
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      padding: '9px 10px',
                      fontSize: 14,
                      lineHeight: 1.35,
                      color: C.text,
                      fontFamily: 'inherit',
                    }}
                  />
                  <button
                    type="button"
                    onClick={askAtlasAnswer}
                    disabled={!canAskAtlas}
                    aria-label="Ask ATLAS"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 18,
                      border: 'none',
                      background: canAskAtlas ? C.blue : '#D1D1D6',
                      color: '#FFFFFF',
                      cursor: canAskAtlas ? 'pointer' : 'default',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SendHorizontal size={16} strokeWidth={2.3} />
                  </button>
                </div>
              </div>
            </>
          )}

          {view === 'support' && (
            <>
              {sourceUnavailable && (
                <div style={{ margin: '12px 14px 0', padding: '10px 12px', borderRadius: 14, background: '#FFF8E8', border: '1px solid #F2D27B', color: '#8A5A00', fontSize: 12.2, lineHeight: 1.35 }}>
                  Support chat is still connecting. Replies will appear here shortly.
                </div>
              )}

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 9, background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)' }}>
                {loading ? (
                  <SkeletonRows count={3} />
                ) : supportThreadMessages.length ? (
                  supportThreadMessages.map(message => (
                    <SupportBubbleMessage key={message.id} message={message} />
                  ))
                ) : (
                  <div style={{
                    margin: 'auto 0',
                    padding: 18,
                    borderRadius: 18,
                    background: C.surfaceAlt,
                    border: `1px solid ${C.border}`,
                    color: C.textSecondary,
                    fontSize: 13,
                    lineHeight: 1.45,
                    textAlign: 'center',
                  }}>
                    Need help with an order, prescription, lab, or billing question? Send us a message here.
                  </div>
                )}
                <div ref={scrollRef} />
              </div>

              <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(247,248,250,0.92)', padding: 12, display: 'grid', gap: 9 }}>
                {error && <div style={{ fontSize: 12, color: C.red }}>{error}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, padding: 5, border: `1px solid ${C.border}`, borderRadius: 22, background: '#FFFFFF', alignItems: 'end' }}>
                  <textarea
                    value={body}
                    onChange={event => setBody(event.target.value)}
                    onKeyDown={onComposerKeyDown}
                    rows={1}
                    placeholder="Message support"
                    style={{
                      minHeight: 36,
                      maxHeight: 96,
                      resize: 'none',
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      padding: '9px 10px',
                      fontSize: 14,
                      lineHeight: 1.35,
                      color: C.text,
                      fontFamily: 'inherit',
                    }}
                  />
                  <button
                    type="button"
                    onClick={sendSupportMessage}
                    disabled={!canSend}
                    aria-label="Send support message"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 18,
                      border: 'none',
                      background: canSend ? C.blue : '#D1D1D6',
                      color: '#FFFFFF',
                      cursor: canSend ? 'pointer' : 'default',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SendHorizontal size={16} strokeWidth={2.3} />
                  </button>
                </div>
                <Link
                  to={fullMessagesHref}
                  onClick={() => setOpen(false)}
                  style={{
                    justifySelf: 'center',
                    color: C.blue,
                    textDecoration: 'none',
                    fontSize: 12.5,
                    fontWeight: 760,
                  }}
                >
                  Open full Messages
                </Link>
              </div>
            </>
          )}

          {activeRequestConfig && (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, display: 'grid', alignContent: 'start', gap: 13, background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)' }}>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: '#FFFFFF', padding: 14, display: 'grid', gridTemplateColumns: '38px minmax(0, 1fr)', gap: 12, alignItems: 'start', boxShadow: '0 8px 22px rgba(15, 23, 42, 0.04)' }}>
                <span style={{ width: 38, height: 38, borderRadius: 10, background: `${activeRequestConfig.accent}14`, color: activeRequestConfig.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {ActiveRequestIcon && <ActiveRequestIcon size={20} strokeWidth={2.2} />}
                </span>
                <span style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                  <strong style={{ color: C.text, fontSize: 14.5, lineHeight: 1.25 }}>{activeRequestConfig.threadLabel}</strong>
                  <span style={{ color: C.textSecondary, fontSize: 12.5, lineHeight: 1.4 }}>{activeRequestConfig.subtitle}</span>
                </span>
              </div>

              {requestSent ? (
                <div style={{ border: '1px solid #A7E7CC', borderRadius: 8, background: '#EAF8F1', color: C.greenDark, padding: 14, display: 'grid', gap: 10 }}>
                  <strong style={{ fontSize: 14 }}>{requestSent}</strong>
                  <span style={{ fontSize: 12.5, lineHeight: 1.4 }}>We sent it to the support team and created a Messages thread for follow-up.</span>
                  <button
                    type="button"
                    onClick={() => setRequestSent('')}
                    style={{
                      height: 36,
                      border: '1px solid #A7E7CC',
                      borderRadius: 18,
                      background: '#FFFFFF',
                      color: C.greenDark,
                      cursor: 'pointer',
                      fontSize: 12.8,
                      fontWeight: 780,
                    }}
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ color: C.text, fontSize: 12.5, fontWeight: 780 }}>Summary</span>
                    <input
                      value={requestTitle}
                      onChange={event => setRequestTitle(event.target.value)}
                      placeholder={activeRequestConfig.titlePlaceholder}
                      style={{
                        width: '100%',
                        minHeight: 42,
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        background: '#FFFFFF',
                        color: C.text,
                        padding: '0 12px',
                        fontSize: 13.5,
                        boxSizing: 'border-box',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ color: C.text, fontSize: 12.5, fontWeight: 780 }}>Details</span>
                    <textarea
                      value={requestDetails}
                      onChange={event => setRequestDetails(event.target.value)}
                      placeholder={activeRequestConfig.detailsPlaceholder}
                      rows={5}
                      style={{
                        width: '100%',
                        minHeight: 128,
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        background: '#FFFFFF',
                        color: C.text,
                        padding: 12,
                        fontSize: 13.5,
                        lineHeight: 1.4,
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                      }}
                    />
                  </label>
                  {requestError && <div style={{ fontSize: 12, color: C.red }}>{requestError}</div>}
                  <button
                    type="button"
                    onClick={sendAtlasRequest}
                    disabled={!requestCanSend}
                    style={{
                      height: 42,
                      border: 'none',
                      borderRadius: 21,
                      background: requestCanSend ? 'linear-gradient(180deg, #0A84FF 0%, #0070F0 100%)' : '#D1D1D6',
                      color: '#FFFFFF',
                      cursor: requestCanSend ? 'pointer' : 'default',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: 13.5,
                      fontWeight: 820,
                      boxShadow: requestCanSend ? C.blueShadow : 'none',
                    }}
                  >
                    {requestSending ? 'Sending...' : activeRequestConfig.submitLabel}
                    <SendHorizontal size={16} strokeWidth={2.3} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {!hideTrigger && (
        <button
          type="button"
          className="kis-support-bubble-trigger"
          data-tour-id="support-widget"
          onClick={() => setOpen(current => !current)}
          aria-label={open ? 'Close support atlas' : 'Open support atlas'}
          style={{
            height: 56,
            minWidth: 56,
            borderRadius: 28,
            border: '1px solid rgba(255,255,255,0.34)',
            background: open ? 'linear-gradient(180deg, #1B8DFF 0%, #006BE6 100%)' : 'linear-gradient(180deg, #05C875 0%, #00A85F 100%)',
            color: '#FFFFFF',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 9,
            padding: '0 18px',
            boxShadow: '0 18px 44px rgba(0, 122, 255, 0.22), 0 8px 22px rgba(0, 191, 109, 0.18), inset 0 1px 0 rgba(255,255,255,0.24)',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 820,
            position: 'relative',
          }}
        >
          <AtlasIconMark size={30} active={open} />
          <span className="kis-support-bubble-label">Support</span>
          {totalUnreadCount > 0 && (
            <span style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 10,
              background: C.red,
              color: '#FFFFFF',
              border: '2px solid #FFFFFF',
              fontSize: 10.5,
              fontWeight: 850,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
            </span>
          )}
        </button>
      )}
    </div>
  )
}

function SignatureDocumentsBanner({ count }) {
  if (!count) return null
  return (
    <Link
      to="/account#forms"
      className="kis-signature-banner"
      style={{
        height: 38,
        borderRadius: 19,
        border: '1px solid #F2D27B',
        background: '#FFF8E8',
        color: '#9A5B00',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 12px',
        fontSize: 12.5,
        fontWeight: 760,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <FileText size={15} strokeWidth={2.2} />
      <span className="kis-signature-banner-copy">
        {count} pending document{count === 1 ? '' : 's'}
      </span>
      <span className="kis-signature-banner-open" style={{ color: C.text, fontWeight: 820 }}>Open</span>
    </Link>
  )
}

function isPendingSignatureAction(action = {}) {
  const status = String(action.status || '').toLowerCase()
  if (/complete|completed|signed|accepted|verified|processing|review|current/.test(status)) return false
  if (/pending|draft|request|need|not_started/.test(status)) return true
  return ['consent', 'verification', 'form'].includes(action.kind)
}

function NotificationRow({ item, onActivate, onClose }) {
  const Icon = NOTIFICATION_ICONS[item.type] || AlertCircle
  const onClick = () => {
    onActivate?.(item)
    onClose?.()
  }
  const content = (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '34px minmax(0, 1fr)',
      gap: 11,
      padding: '12px 14px',
      borderRadius: 14,
      background: item.unread ? '#F3F8FF' : item.attention ? '#FFF8E8' : '#FFFFFF',
      border: `1px solid ${item.unread ? '#C8E0FF' : item.attention ? '#F2D27B' : C.border}`,
    }}>
      <span style={{
        width: 34,
        height: 34,
        borderRadius: 17,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: item.unread ? '#E7F1FF' : item.attention ? '#FFF1C7' : C.surfaceAlt,
        color: item.unread ? C.blue : item.attention ? C.orange : C.textSecondary,
      }}>
        <Icon size={17} strokeWidth={2.1} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <strong style={{
            minWidth: 0,
            flex: 1,
            fontSize: 13.5,
            fontWeight: 760,
            color: C.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {item.title}
          </strong>
          {item.unread && <span style={{ width: 7, height: 7, borderRadius: 4, background: C.blue, flexShrink: 0 }} />}
          {!item.unread && (item.type === 'message' || item.read_at) && (
            <span style={{
              flexShrink: 0,
              borderRadius: 999,
              background: C.surfaceAlt,
              color: C.textTertiary,
              fontSize: 10.5,
              fontWeight: 740,
              padding: '2px 6px',
            }}>
              Read
            </span>
          )}
        </span>
        {item.body && (
          <span style={{
            display: 'block',
            marginTop: 4,
            fontSize: 12.3,
            lineHeight: 1.35,
            color: C.textSecondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {item.body}
          </span>
        )}
        {item.created_at && (
          <span style={{ display: 'block', marginTop: 5, fontSize: 11.3, color: C.textTertiary }}>
            {notificationTime(item.created_at)}
          </span>
        )}
      </span>
    </div>
  )

  if (!item.href) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ display: 'block', width: '100%', padding: 0, border: 0, background: 'transparent', color: 'inherit', textAlign: 'left', cursor: item.unread ? 'pointer' : 'default' }}
      >
        {content}
      </button>
    )
  }
  return (
    <Link to={item.href} onClick={onClick} style={{ color: 'inherit', textDecoration: 'none' }}>
      {content}
    </Link>
  )
}

function notificationKey(item = {}) {
  return item.thread || item.dedupeKey || item.notificationId || item.id
}

function readLocalNotificationKeys() {
  if (typeof window === 'undefined') return new Set()
  try {
    const parsed = JSON.parse(window.localStorage.getItem(NOTIFICATION_READ_STORAGE_KEY) || '[]')
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.map(item => String(item || '')).filter(Boolean))
  } catch {
    return new Set()
  }
}

function saveLocalNotificationKeys(keys) {
  if (typeof window === 'undefined') return
  const values = Array.from(keys).filter(Boolean).slice(-200)
  window.localStorage.setItem(NOTIFICATION_READ_STORAGE_KEY, JSON.stringify(values))
}

function rememberLocalNotificationReads(items = []) {
  const list = Array.isArray(items) ? items : [items]
  const keys = readLocalNotificationKeys()
  list
    .filter(item => item && item.type !== 'message')
    .map(notificationKey)
    .filter(Boolean)
    .forEach(key => keys.add(key))
  saveLocalNotificationKeys(keys)
}

function applyLocalNotificationReads(notifications = []) {
  const readKeys = readLocalNotificationKeys()
  let clearedAlertCount = 0
  const nextNotifications = (notifications || []).map(item => {
    const key = notificationKey(item)
    if (!key || item.type === 'message' || !item.unread || !readKeys.has(key)) return item
    clearedAlertCount += 1
    return { ...item, unread: false, unreadCount: 0, read_at: item.read_at || new Date().toISOString() }
  })
  return { notifications: nextNotifications, clearedAlertCount }
}

function notificationReadPayload(items = []) {
  const list = Array.isArray(items) ? items : [items]
  const notificationIds = [...new Set(list.map(item => item?.notificationId).filter(Boolean))]
  const dedupeKeys = [...new Set(list.map(item => item?.dedupeKey || (!item?.thread ? item?.id : null)).filter(Boolean))]
  return { notificationIds, dedupeKeys }
}

function NotificationBell({ compact = false }) {
  const buttonSize = compact ? 34 : 38
  const badgeSize = compact ? 17 : 18
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [marking, setMarking] = useState(false)
  const [limit, setLimit] = useState(10)
  const [data, setData] = useState({ notifications: [], totalCount: 0, unreadMessageCount: 0, alertCount: 0, hasMore: false })
  const rootRef = useRef(null)
  const autoReadRef = useRef(new Set())

  const loadNotifications = useCallback(async () => {
    try {
      const next = await apiGet(`/api/mso/notifications?limit=${limit}`)
      const localState = applyLocalNotificationReads(next.notifications || [])
      const alertCount = Math.max(0, (next.alertCount || 0) - localState.clearedAlertCount)
      setData({
        notifications: localState.notifications,
        totalCount: Math.max(0, (next.unreadMessageCount || 0) + alertCount),
        unreadMessageCount: next.unreadMessageCount || 0,
        alertCount,
        hasMore: Boolean(next.hasMore),
        nextLimit: next.nextLimit || null,
        sourceUnavailable: next.sourceUnavailable,
      })
    } catch {
      setData(current => ({ ...current, sourceUnavailable: true }))
    } finally {
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    const initialLoad = window.setTimeout(loadNotifications, 0)
    const interval = window.setInterval(() => {
      if (!document.hidden) loadNotifications()
    }, 30000)
    const onFocus = () => loadNotifications()
    const onRefresh = () => loadNotifications()
    window.addEventListener('focus', onFocus)
    window.addEventListener('kis:notifications:refresh', onRefresh)
    return () => {
      window.clearTimeout(initialLoad)
      window.clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('kis:notifications:refresh', onRefresh)
    }
  }, [loadNotifications])

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  const countLabel = useMemo(() => {
    const count = data.totalCount || 0
    if (count <= 0) return ''
    return count > 9 ? '9+' : String(count)
  }, [data.totalCount])

  const markAlertNotificationsRead = useCallback((items) => {
    const list = (Array.isArray(items) ? items : [items])
      .filter(item => item?.unread && item.type !== 'message')
    if (!list.length) return

    rememberLocalNotificationReads(list)
    const itemKeys = new Set(list.map(notificationKey).filter(Boolean))
    setData(current => {
      const decrement = (current.notifications || []).filter(notification =>
        itemKeys.has(notificationKey(notification)) && notification.unread && notification.type !== 'message'
      ).length
      return {
        ...current,
        notifications: (current.notifications || []).map(notification =>
          itemKeys.has(notificationKey(notification))
            ? { ...notification, unread: false, unreadCount: 0, read_at: notification.read_at || new Date().toISOString() }
            : notification
        ),
        alertCount: Math.max(0, (current.alertCount || 0) - decrement),
        totalCount: Math.max(0, (current.totalCount || 0) - decrement),
      }
    })

    apiPost('/api/mso/notifications/read', notificationReadPayload(list))
      .then(loadNotifications)
      .catch(() => loadNotifications())
  }, [loadNotifications])

  const markNotificationsRead = async () => {
    if (marking) return
    setMarking(true)
    try {
      const unreadAlerts = (data.notifications || []).filter(item => item.unread && item.type !== 'message')
      rememberLocalNotificationReads(unreadAlerts)
      await apiPost('/api/mso/notifications/read', {
        all: true,
        ...notificationReadPayload(unreadAlerts),
      })
      await loadNotifications()
      if (data.unreadMessageCount > 0) {
        window.dispatchEvent(new CustomEvent('kis:thread-read', { detail: { thread: 'all' } }))
      }
    } finally {
      setMarking(false)
    }
  }

  const markNotificationRead = item => {
    if (!item?.unread) return
    if (item.type !== 'message' || !item.thread) {
      markAlertNotificationsRead(item)
      return
    }
    const decrement = item.unreadCount || 1
    setData(current => ({
      ...current,
      notifications: (current.notifications || []).map(notification =>
        notification.thread === item.thread
          ? { ...notification, unread: false, unreadCount: 0 }
          : notification
      ),
      unreadMessageCount: Math.max(0, (current.unreadMessageCount || 0) - decrement),
      totalCount: Math.max(0, (current.totalCount || 0) - decrement),
    }))
    apiPost('/api/mso/notifications/read', { thread: item.thread })
      .then(loadNotifications)
      .catch(() => loadNotifications())
    window.dispatchEvent(new CustomEvent('kis:thread-read', { detail: { thread: item.thread } }))
  }

  useEffect(() => {
    if (!open || loading) return
    const unreadAlerts = (data.notifications || []).filter(item => {
      const key = notificationKey(item)
      return item.unread && item.type !== 'message' && key && !autoReadRef.current.has(key)
    })
    if (!unreadAlerts.length) return
    unreadAlerts.forEach(item => autoReadRef.current.add(notificationKey(item)))
    markAlertNotificationsRead(unreadAlerts)
  }, [open, loading, data.notifications, markAlertNotificationsRead])

  return (
    <div ref={rootRef} className="kis-notification-root" style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(current => !current)}
        aria-label="Open notifications"
        data-tour-id="notifications"
        style={{
          width: buttonSize,
          height: buttonSize,
          borderRadius: buttonSize / 2,
          border: `1px solid ${open ? '#C8E0FF' : C.border}`,
          background: open ? '#F3F8FF' : '#FFFFFF',
          color: open ? C.blue : C.text,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          boxShadow: open ? '0 8px 24px rgba(0, 122, 255, 0.12)' : 'none',
        }}
      >
        <Bell size={compact ? 16 : 18} strokeWidth={2.1} />
        {countLabel && (
          <span style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: badgeSize,
            height: badgeSize,
            padding: compact ? '0 4px' : '0 5px',
            borderRadius: badgeSize / 2,
            background: C.red,
            color: '#FFFFFF',
            border: '2px solid #FFFFFF',
            fontSize: compact ? 10 : 10.5,
            fontWeight: 800,
            lineHeight: compact ? '13px' : '14px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {countLabel}
          </span>
        )}
      </button>

      {open && (
        <div className="kis-notification-popover" style={{
          position: 'absolute',
          top: compact ? 42 : 48,
          right: 0,
          width: 'min(390px, calc(100vw - 28px))',
          maxHeight: 'min(620px, calc(100vh - 88px))',
          overflow: 'hidden',
          borderRadius: 24,
          background: 'rgba(255,255,255,0.98)',
          border: `1px solid ${C.border}`,
          boxShadow: '0 24px 70px rgba(0,0,0,0.18)',
          zIndex: 80,
          backdropFilter: 'blur(18px)',
        }}>
          <div style={{
            padding: '16px 16px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            borderBottom: `1px solid ${C.border}`,
          }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 790, color: C.text }}>Notifications</div>
              <div style={{ marginTop: 3, fontSize: 12.3, color: C.textSecondary }}>
                {data.totalCount > 0 ? `${data.totalCount} item${data.totalCount === 1 ? '' : 's'} need attention` : 'No unread items'}
              </div>
            </div>
            {data.totalCount > 0 && (
              <button
                type="button"
                onClick={markNotificationsRead}
                disabled={marking}
                style={{
                  height: 32,
                  border: `1px solid ${C.border}`,
                  borderRadius: 16,
                  background: '#FFFFFF',
                  color: C.text,
                  cursor: marking ? 'default' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0 10px',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <CheckCheck size={14} />
                {marking ? 'Saving' : 'Read'}
              </button>
            )}
          </div>

          <div style={{ padding: 12, display: 'grid', gap: 9, overflowY: 'auto', maxHeight: 'min(540px, calc(100vh - 170px))' }}>
            {loading ? (
              <SkeletonRows count={3} />
            ) : data.notifications.length === 0 ? (
              <div style={{
                padding: 20,
                borderRadius: 18,
                background: C.surfaceAlt,
                border: `1px solid ${C.border}`,
                color: C.textSecondary,
                fontSize: 13,
                lineHeight: 1.4,
              }}>
                You&apos;re all caught up.
              </div>
            ) : (
              data.notifications.map(item => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  onActivate={markNotificationRead}
                  onClose={() => setOpen(false)}
                />
              ))
            )}
            {data.hasMore && (
              <button
                type="button"
                onClick={() => setLimit(data.nextLimit || limit + 10)}
                style={{
                  height: 38,
                  border: `1px solid ${C.border}`,
                  borderRadius: 19,
                  background: '#FFFFFF',
                  color: C.text,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 730,
                }}
              >
                Load more
              </button>
            )}
            {data.sourceUnavailable && (
              <div style={{ fontSize: 11.8, color: C.textTertiary, padding: '0 4px 4px' }}>
                Some live updates are still syncing.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function Shell({ patient }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const [headerAction, setHeaderAction] = useState(null)
  const [signatureActions, setSignatureActions] = useState([])
  const hideShopTab = patientHidesShopTab(patient)
  const navItems = patient?.mso
    ? MSO_NAV.filter(item => !(hideShopTab && item.id === 'shop'))
    : LEGACY_NAV
  const mobilePrimaryIds = patient?.mso
    ? (hideShopTab ? ['dashboard', 'messages', 'labs'] : ['dashboard', 'shop', 'messages', 'labs'])
    : ['dashboard', 'messages', 'visits', 'prescriptions', 'labs']
  const mobileNavItems = mobilePrimaryIds
    .map(id => navItems.find(item => item.id === id))
    .filter(Boolean)
  const showMobileSupportNav = Boolean(patient?.mso)
  const mobileBottomItemCount = mobileNavItems.length + (showMobileSupportNav ? 1 : 0)

  const loadSignatureActions = useCallback(async () => {
    if (!patient?.mso) {
      setSignatureActions([])
      return
    }
    try {
      const account = await apiGet('/api/mso/account')
      setSignatureActions((account.signatureActions || []).filter(isPendingSignatureAction))
    } catch {
      setSignatureActions([])
    }
  }, [patient?.mso])

  useEffect(() => {
    const initialLoad = window.setTimeout(loadSignatureActions, 0)
    window.addEventListener('focus', loadSignatureActions)
    return () => {
      window.clearTimeout(initialLoad)
      window.removeEventListener('focus', loadSignatureActions)
    }
  }, [loadSignatureActions])

  const onSignOut = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const initials = (patient?.full_name || patient?.fullName || patient?.email || 'U')
    .split(/\s+/)
    .map(s => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  const activeItem = navItems.find(item =>
    (item.match || [item.path]).some(path => location.pathname === path || location.pathname.startsWith(`${path}/`))
  )
  const activePage = activeItem?.id || (location.pathname.replace('/', '') || 'dashboard')
  const titleMap = Object.fromEntries(navItems.map(n => [n.id, n.label]))
  const patientDisplayName = patient?.full_name || patient?.fullName || [patient?.first_name, patient?.last_name].filter(Boolean).join(' ').trim() || 'Patient'
  const patientIdLabel = patient?.patient_number || patient?.patientNumber || 'Pending'
  const patientIdDisplay = `Patient ID ${patientIdLabel}`
  const subtitleMap = {
    account: `${patientDisplayName} | ${patientIdDisplay}`,
    forms: 'Your health story, organized.',
    labs: 'Know what to do next.',
    messages: 'Ask the right team.',
    orders: 'Track every request clearly.',
    visits: 'Get clear answers from your care team.',
    help: 'Find answers fast.',
  }
  const activeTitle = titleMap[activePage] || 'KIS Rx'
  const activeSubtitle = subtitleMap[activePage]
  const isDashboardHeader = activePage === 'dashboard'
  const isMessagesPage = activePage === 'messages'
  const isCompactHeader = activePage === 'labs' || isMessagesPage
  const shellHeaderHeight = isCompactHeader ? 52 : 64

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setMobileOpen(false)
      setSupportOpen(false)
    }, 0)
    return () => window.clearTimeout(timeout)
  }, [location.pathname])

  const renderSidebarContent = (contentCollapsed = collapsed, isMobileSidebar = false) => (
    <>
      <div style={{
        padding: contentCollapsed ? '18px 14px 12px' : '20px 18px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: contentCollapsed ? 'center' : 'space-between',
        gap: 10,
      }}>
        <Logo size={34} withWordmark={!contentCollapsed} />
        {!contentCollapsed && (
          <button
            type="button"
            onClick={() => {
              if (isMobileSidebar) {
                setMobileOpen(false)
                return
              }
              setCollapsed(true)
            }}
            className="kis-sidebar-collapse-inside"
            aria-label={isMobileSidebar ? 'Close menu' : 'Collapse sidebar'}
            title={isMobileSidebar ? 'Close menu' : 'Collapse sidebar'}
            style={{
              width: 32,
              height: 32,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              background: 'rgba(255,255,255,0.74)',
              color: C.textSecondary,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PanelLeftClose size={16} />
          </button>
        )}
      </div>

      {contentCollapsed && !isMobileSidebar && (
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="Expand sidebar"
          style={{
            width: 36,
            height: 36,
            border: `1px solid ${C.border}`,
            borderRadius: 18,
            background: 'rgba(255,255,255,0.74)',
            color: C.textSecondary,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 8px',
          }}
        >
          <PanelLeftOpen size={16} />
        </button>
      )}

      <nav style={{ flex: 1, padding: contentCollapsed ? '8px 10px' : '10px 12px', overflowY: 'auto' }}>
        {navItems.map(item => (
          <NavItem
            key={item.id}
            item={item}
            active={activePage === item.id}
            collapsed={contentCollapsed}
            onClick={() => setMobileOpen(false)}
          />
        ))}
      </nav>

      <div style={{ padding: contentCollapsed ? '12px 10px 16px' : '14px 12px 18px' }}>
        <div className="kis-sidebar-user-card" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: contentCollapsed ? 'center' : 'space-between',
          gap: 10,
          padding: contentCollapsed ? 0 : 10,
          borderRadius: 18,
          background: contentCollapsed ? 'transparent' : 'rgba(255,255,255,0.62)',
          border: contentCollapsed ? 'none' : `1px solid ${C.border}`,
          boxShadow: contentCollapsed ? 'none' : 'inset 0 1px 0 rgba(255,255,255,0.8), 0 8px 22px rgba(17,24,39,0.045)',
        }}>
          <Link
            to="/account"
            onClick={() => setMobileOpen(false)}
            title="Open account"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flex: 1,
              minWidth: 0,
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            <Avatar initials={initials} size={34} gradient="linear-gradient(135deg, #00BF6D, #007AFF)" />
            {!contentCollapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 760,
                  color: C.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {patientDisplayName}
                </div>
                <div style={{
                  fontSize: 11.5,
                  color: C.textTertiary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginTop: 2,
                }}>
                  {patientIdDisplay}
                </div>
              </div>
            )}
          </Link>
          {!contentCollapsed && (
            <button
              onClick={onSignOut}
              title="Sign out"
              style={{
                width: 32,
                height: 32,
                background: 'rgba(255,255,255,0.82)',
                border: `1px solid ${C.border}`,
                cursor: 'pointer',
                borderRadius: 16,
                boxShadow: '0 6px 16px rgba(17,24,39,0.055)',
                color: C.textSecondary,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LogOut size={15} />
            </button>
          )}
        </div>
      </div>
    </>
  )
  const desktopSidebarContent = renderSidebarContent(collapsed, false)
  const mobileSidebarContent = renderSidebarContent(false, true)

  return (
    <div className={`kis-app-shell${isMessagesPage ? ' kis-app-shell-messages' : ''}`} style={{
      display: 'flex',
      minHeight: '100vh',
      height: isMessagesPage ? '100vh' : undefined,
      overflow: isMessagesPage ? 'hidden' : undefined,
      background: 'linear-gradient(180deg, #F8FBFF 0%, #F2F5FA 54%, #EEF2F7 100%)',
    }}>
      <style>{`
        @media (max-width: 900px) {
          .kis-sidebar-desktop { display: none !important; }
          .kis-mobile-menu { display: flex !important; }
          .kis-mobile-bottom-nav {
            display: grid !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            min-height: calc(76px + env(safe-area-inset-bottom)) !important;
            padding: 8px 12px calc(8px + env(safe-area-inset-bottom)) !important;
            border-right: 0 !important;
            border-bottom: 0 !important;
            border-left: 0 !important;
            border-radius: 22px 22px 0 0 !important;
          }
          .kis-app-shell-messages .kis-mobile-bottom-nav {
            min-height: calc(68px + env(safe-area-inset-bottom)) !important;
            padding: 6px 10px calc(6px + env(safe-area-inset-bottom)) !important;
            border-radius: 18px 18px 0 0 !important;
          }
          .kis-app-shell-messages .kis-mobile-bottom-nav a,
          .kis-app-shell-messages .kis-mobile-bottom-nav button {
            min-height: 48px !important;
            border-radius: 15px !important;
          }
          .kis-msg-grid { grid-template-columns: 1fr !important; }
          .kis-shell-header { gap: 8px !important; flex-wrap: wrap !important; align-content: center !important; padding: 0 14px !important; min-height: 58px !important; overflow: visible !important; z-index: 90 !important; }
          .kis-shell-header.kis-shell-header-compact { min-height: 50px !important; }
          .kis-mobile-menu { margin-right: 0 !important; }
          .kis-shell-title {
            flex: 1 1 auto !important;
            min-width: 0 !important;
            overflow: hidden !important;
            white-space: nowrap !important;
            font-size: 18px !important;
            gap: 6px !important;
          }
          .kis-shell-title > span:first-child,
          .kis-dashboard-title-label {
            min-width: 0 !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          .kis-dashboard-title-meta,
          .kis-dashboard-title-separator {
            display: none !important;
          }
          .kis-shell-subtitle { font-size: 13px !important; }
          .kis-shell-header-action {
            order: 5;
            flex: 0 0 100% !important;
            margin: 8px 0 0 0 !important;
          }
          .kis-shell-header-action:has(.account-header-actions) {
            display: none !important;
          }
          .kis-signature-banner {
            width: 38px !important;
            padding: 0 !important;
            justify-content: center !important;
            gap: 0 !important;
          }
          .kis-signature-banner-open,
          .kis-signature-banner-copy {
            display: none !important;
          }
          .kis-notification-root,
          .kis-mobile-menu,
          .kis-signature-banner {
            flex-shrink: 0 !important;
          }
          .kis-notification-root {
            z-index: 120 !important;
          }
          .kis-notification-popover {
            z-index: 140 !important;
          }
          .kis-main-shell { padding: 18px 14px calc(112px + env(safe-area-inset-bottom)) !important; }
          .kis-main-shell.kis-main-shell-messages { padding: 6px 8px calc(74px + env(safe-area-inset-bottom)) !important; overflow: hidden !important; }
          .kis-support-bubble-root {
            inset: auto 0 0 0 !important;
            pointer-events: none !important;
          }
          .kis-support-bubble-trigger {
            display: none !important;
          }
          .kis-pwa-prompt {
            right: 12px !important;
            left: 12px !important;
            bottom: calc(154px + env(safe-area-inset-bottom)) !important;
            width: auto !important;
          }
          .kis-pwa-prompt-actions button { flex: 1 1 132px; justify-content: center; }
          .kis-support-bubble-panel {
            position: fixed !important;
            right: 12px !important;
            left: 12px !important;
            bottom: calc(86px + env(safe-area-inset-bottom)) !important;
            width: auto !important;
            height: min(560px, calc(100vh - 104px)) !important;
            pointer-events: auto !important;
          }
          .kis-atlas-action-grid { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 901px) {
          .kis-sidebar-mobile, .kis-sidebar-backdrop, .kis-mobile-bottom-nav { display: none !important; }
        }
        @media (max-width: 760px) {
          .portal-grid-two, .portal-grid-three { grid-template-columns: 1fr !important; }
          .portal-page-intro { grid-template-columns: 1fr !important; align-items: start !important; gap: 12px !important; margin-bottom: 16px !important; }
          .portal-page-title { font-size: 28px !important; line-height: 1.08 !important; }
          .portal-page-intro-actions { justify-content: flex-start !important; }
          .portal-list-shell-header { padding: 15px 16px !important; align-items: flex-start !important; }
          .portal-row-link { padding: 14px 16px !important; }
          .portal-row-link-has-icon { grid-template-columns: 34px minmax(0, 1fr) !important; }
          .portal-row-link-no-icon { grid-template-columns: minmax(0, 1fr) !important; }
          .portal-row-link-right { justify-self: start !important; margin-top: 4px !important; }
          .portal-row-link-has-icon .portal-row-link-right { grid-column: 2 !important; }
          .portal-row-link-no-icon .portal-row-link-right { grid-column: 1 !important; }
          .portal-mobile-stack { grid-template-columns: 1fr !important; }
          .portal-prescription-row { grid-template-columns: 1fr !important; padding: 16px !important; }
          .portal-account-section-header {
            grid-template-columns: minmax(0, 1fr) auto !important;
            align-items: start !important;
          }
          .portal-account-section-toggle {
            grid-column: 1 !important;
            grid-row: 1 !important;
          }
          .portal-account-section-chevron {
            grid-column: 2 !important;
            grid-row: 1 !important;
            align-self: start !important;
          }
          .portal-account-section-action {
            grid-column: 1 / -1 !important;
            grid-row: 2 !important;
            justify-content: flex-start !important;
          }
          .portal-mobile-actions { width: 100% !important; justify-content: flex-start !important; }
          .portal-mobile-full { width: 100% !important; }
          .portal-mobile-full button, .portal-mobile-full a { width: 100%; justify-content: center; }
        }
        @media (max-width: 560px) {
          .kis-shell-header { padding: 0 10px !important; }
          .kis-shell-header.kis-shell-header-compact { min-height: 48px !important; }
          .kis-shell-title { font-size: 17px !important; }
          .kis-shell-subtitle { display: none !important; }
          .kis-shell-title-separator { display: none !important; }
          .kis-main-shell { padding: 12px 10px calc(124px + env(safe-area-inset-bottom)) !important; }
          .kis-main-shell.kis-main-shell-messages { padding: 5px 6px calc(76px + env(safe-area-inset-bottom)) !important; }
          .portal-page-title { font-size: 25px !important; }
        }
        button:hover { opacity: 0.96; }
        input:focus, textarea:focus, select:focus {
          border-color: rgba(0, 122, 255, 0.42) !important;
          box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.10) !important;
          outline: none;
        }
      `}</style>

      <aside className="kis-sidebar-desktop" style={{
        width: collapsed ? 72 : 252,
        background: C.sidebarBg,
        borderRight: `1px solid ${C.border}`,
        backdropFilter: 'blur(26px) saturate(1.18)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'width 0.2s ease',
        height: '100vh',
        position: 'sticky',
        top: 0,
        zIndex: 30,
        boxShadow: '8px 0 30px rgba(17,24,39,0.035)',
      }}>{desktopSidebarContent}</aside>

      {mobileOpen && (
        <>
          <div className="kis-sidebar-backdrop" onClick={() => setMobileOpen(false)} style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17,24,39,0.34)',
            backdropFilter: 'blur(6px)',
            zIndex: 9000,
          }} />
          <aside className="kis-sidebar-mobile" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: 'min(88vw, 360px)',
            maxWidth: 'calc(100vw - 56px)',
            background: 'rgba(255,255,255,0.92)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9010,
            boxShadow: C.shadowStrong,
            backdropFilter: 'blur(26px) saturate(1.16)',
            overflow: 'hidden',
          }}>{mobileSidebarContent}</aside>
        </>
      )}

      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        height: isMessagesPage ? '100vh' : undefined,
      }}>
        <header className={`kis-shell-header${isCompactHeader ? ' kis-shell-header-compact' : ''}`} style={{
          height: headerAction ? 'auto' : shellHeaderHeight,
          minHeight: shellHeaderHeight,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(250,252,255,0.78) 100%)',
          borderBottom: `1px solid ${C.border}`,
          backdropFilter: 'blur(26px) saturate(1.18)',
          display: 'flex',
          alignItems: 'center',
          padding: headerAction ? '8px 28px' : isCompactHeader ? '0 22px' : '0 28px',
          position: 'sticky',
          top: 0,
          zIndex: 20,
          boxShadow: '0 1px 0 rgba(255,255,255,0.74), 0 14px 34px rgba(15,23,42,0.045)',
        }}>
          <button onClick={() => setMobileOpen(true)} className="kis-mobile-menu" style={{
            background: 'rgba(255,255,255,0.78)',
            border: `1px solid ${C.border}`,
            width: 36,
            height: 36,
            marginRight: 12,
            cursor: 'pointer',
            color: C.text,
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 18,
            boxShadow: C.shadowSoft,
          }}>
            <Menu size={18} />
          </button>
          <h1 className="kis-shell-title" style={{
            fontSize: isDashboardHeader || isCompactHeader ? 18 : 20,
            fontWeight: 820,
            color: C.text,
            letterSpacing: 0,
            margin: 0,
            flex: headerAction ? '0 0 auto' : 1,
            display: 'flex',
            alignItems: isDashboardHeader ? 'center' : 'baseline',
            gap: isDashboardHeader ? 10 : isCompactHeader ? 7 : 9,
            minWidth: 0,
            flexWrap: isDashboardHeader ? 'nowrap' : 'wrap',
            overflow: isDashboardHeader ? 'hidden' : 'visible',
            whiteSpace: isDashboardHeader ? 'nowrap' : 'normal',
          }}>
            {isDashboardHeader ? (
              <>
                <span className="kis-dashboard-title-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Dashboard</span>
                <span className="kis-dashboard-title-separator" style={{ color: C.textTertiary, fontWeight: 650 }}>|</span>
                <span className="kis-dashboard-title-meta" style={{ color: C.textSecondary, fontWeight: 720, overflow: 'hidden', textOverflow: 'ellipsis' }}>{dashboardHeaderDate()}</span>
                <span className="kis-dashboard-title-separator" style={{ color: C.textTertiary, fontWeight: 650 }}>|</span>
                <span className="kis-dashboard-title-meta" style={{ color: C.textSecondary, fontWeight: 720, overflow: 'hidden', textOverflow: 'ellipsis' }}>{dashboardHeaderGreeting(patient)}</span>
              </>
            ) : (
              <>
                <span>{activeTitle}</span>
                {activeSubtitle && (
                  <>
                    <span className="kis-shell-title-separator" aria-hidden="true" style={{ color: C.textTertiary, fontWeight: 520 }}>|</span>
                    <span className="kis-shell-subtitle" style={{ color: C.textSecondary, fontSize: isCompactHeader ? 13.5 : 15, fontWeight: 640 }}>
                      {activeSubtitle}
                    </span>
                  </>
                )}
              </>
            )}
          </h1>
          {headerAction && (
            <div className="kis-shell-header-action" style={{ flex: 1, minWidth: 0, marginLeft: 22, display: 'flex', alignItems: 'center' }}>
              {headerAction}
            </div>
          )}
          {isDashboardHeader && signatureActions.length > 0 && (
            <SignatureDocumentsBanner count={signatureActions.length} />
          )}
          <NotificationBell compact={isCompactHeader} />
        </header>
        <main
          className={`kis-main-shell${isMessagesPage ? ' kis-main-shell-messages' : ''}`}
          style={{
            flex: 1,
            minHeight: 0,
            padding: isMessagesPage ? '20px 12px' : isCompactHeader ? '18px 24px 96px' : '28px 30px 110px',
            overflow: isMessagesPage ? 'hidden' : 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div style={{
            width: '100%',
            maxWidth: isMessagesPage ? 'none' : 1440,
            margin: '0 auto',
            height: isMessagesPage ? '100%' : undefined,
            minHeight: 0,
          }}>
            <Outlet context={{ setHeaderAction, patient }} />
          </div>
        </main>
      </div>
      <SupportBubble
        patient={patient}
        open={supportOpen}
        onOpenChange={setSupportOpen}
        hideTrigger={isMessagesPage}
      />
      {mobileBottomItemCount > 0 && (
        <nav
          className="kis-mobile-bottom-nav"
          aria-label="Primary navigation"
          style={{
            position: 'fixed',
            left: 10,
            right: 10,
            bottom: 'calc(10px + env(safe-area-inset-bottom))',
            zIndex: 34,
            minHeight: 66,
            padding: '7px 8px',
            borderRadius: 24,
            border: `1px solid ${C.border}`,
            background: 'rgba(255,255,255,0.94)',
            boxShadow: '0 20px 58px rgba(15,23,42,0.16)',
            backdropFilter: 'blur(24px) saturate(1.16)',
            display: 'none',
            gridTemplateColumns: `repeat(${mobileBottomItemCount}, minmax(0, 1fr))`,
            gap: 4,
          }}
        >
          {mobileNavItems.map(item => {
            const Icon = item.icon
            const active = activePage === item.id
            return (
              <Link
                key={item.id}
                to={item.path}
                aria-current={active ? 'page' : undefined}
                onClick={() => {
                  if (active && item.id === 'shop') {
                    window.dispatchEvent(new CustomEvent('kis:shop:reset'))
                  }
                  setMobileOpen(false)
                  setSupportOpen(false)
                }}
                style={{
                  minWidth: 0,
                  minHeight: 52,
                  borderRadius: 18,
                  color: active ? C.greenDark : C.textTertiary,
                  background: active ? 'linear-gradient(180deg, #ECFFF6 0%, #F8FFFB 100%)' : 'transparent',
                  textDecoration: 'none',
                  display: 'grid',
                  justifyItems: 'center',
                  alignContent: 'center',
                  gap: 3,
                  fontSize: 10.8,
                  fontWeight: active ? 820 : 720,
                  boxShadow: active ? 'inset 0 0 0 1px rgba(0,191,109,0.14)' : 'none',
                }}
              >
                <Icon size={20} strokeWidth={active ? 2.35 : 2} />
                <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.label === 'Prescriptions' ? 'Rx' : item.label === 'Dashboard' ? 'Home' : item.label}
                </span>
              </Link>
            )
          })}
          {showMobileSupportNav && (
            <button
              type="button"
              className="kis-mobile-support-tab"
              aria-pressed={supportOpen}
              aria-label={supportOpen ? 'Close support' : 'Open support'}
              onPointerDown={event => event.stopPropagation()}
              onClick={() => {
                setMobileOpen(false)
                setSupportOpen(current => !current)
              }}
              style={{
                minWidth: 0,
                minHeight: 52,
                border: 'none',
                borderRadius: 18,
                color: supportOpen ? C.greenDark : C.textTertiary,
                background: supportOpen ? 'linear-gradient(180deg, #ECFFF6 0%, #F8FFFB 100%)' : 'transparent',
                textDecoration: 'none',
                display: 'grid',
                justifyItems: 'center',
                alignContent: 'center',
                gap: 3,
                fontSize: 10.8,
                fontWeight: supportOpen ? 820 : 720,
                boxShadow: supportOpen ? 'inset 0 0 0 1px rgba(0,191,109,0.14)' : 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <Headphones size={20} strokeWidth={supportOpen ? 2.35 : 2} />
              <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Support
              </span>
            </button>
          )}
        </nav>
      )}
      <PushNotificationPrompt patient={patient} />
      <PortalWalkthrough patient={patient} />
    </div>
  )
}
